import { useId, useState } from 'react'

/**
 * Ícono "i" que muestra una explicación al hover, focus (teclado) o tap
 * (mobile — no hay hover en touch, así que el trigger tiene que abrir con un
 * click normal también). Contenido inyectado por prop, nunca hardcodeado
 * inline en cada lugar que lo usa — así el texto de cada métrica del
 * dashboard físiológico vive en un solo lugar (ver `pages/data/metricInfo.ts`).
 *
 * Reutilizable en cualquier página: no depende de nada de /data.
 */
export function InfoTooltip({ label, children }: { label: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false)
  const id = useId()

  return (
    <span className="relative inline-flex">
      <button
        type="button"
        aria-describedby={id}
        aria-label={`Más información: ${label}`}
        className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-text-muted/60 text-[10px] leading-none text-text-muted hover:border-primary hover:text-primary focus-visible:border-primary focus-visible:text-primary"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={(e) => {
          e.stopPropagation()
          // Tap en mobile no dispara hover: el click alterna el estado para
          // que el mismo trigger sirva de botón en touch.
          setOpen((v) => !v)
        }}
      >
        i
      </button>
      {open && (
        <span
          role="tooltip"
          id={id}
          className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-xs w-64 -translate-x-1/2 rounded-md border border-primary/30 bg-surface-elevated p-sm text-left text-xs font-normal normal-case leading-relaxed tracking-normal text-text-secondary shadow-[0_8px_24px_rgba(0,0,0,0.25)]"
        >
          {children}
        </span>
      )}
    </span>
  )
}
